<template>
    <section v-if="hasPagination || (compact && showTotal)" class="pagination-container" :class="{ detailed: showTotal || showPageNumbers, compact }">
        <span v-if="showTotal" class="pagination-total">{{ totalLabel }}: {{ normalizedTotalItems }}</span>
        <nav class="pagination-controls">
            <button v-if="canGoPrevious" type="button" class="back button" @click="$emit('previous-page')">
                <i class="i-andy-chevron-left i-andy-m"></i>
            </button>
            <template v-if="showPageNumbers">
                <template v-for="page in visiblePageNumbers">
                <span v-if="page === 'ellipsis'" :key="page" class="pagination-ellipsis" aria-hidden="true">…</span>
                <button
                    v-else
                    :key="page"
                    type="button"
                    class="page button"
                    :class="{ active: page === normalizedPageNumber }"
                    :aria-current="page === normalizedPageNumber ? 'page' : null"
                    @click="goToPage(page)"
                >
                    {{ page }}
                </button>
                </template>
            </template>
            <button v-if="canGoNext" type="button" class="forward button" @click="$emit('next-page')">
                <i class="i-andy-chevron-right i-andy-m"></i>
            </button>
        </nav>
    </section>
</template>

<script>
import {
    getPaginationTotalPages,
    getVisiblePageNumbers,
    normalizeItemsPerPage,
    normalizePaginationPage,
    normalizePaginationTotal
} from '@/utils/pagination'

export default {
    name: 'PaginationBar',
    props: {
        compact: {
            type: Boolean,
            default: false
        },
        pageNumber: {
            type: [Number, String],
            default: 1
        },
        itemsPerPage: {
            type: [Number, String],
            default: 20
        },
        totalItems: {
            type: [Number, String],
            default: 0
        },
        showPageNumbers: {
            type: Boolean,
            default: false
        },
        showTotal: {
            type: Boolean,
            default: false
        },
        totalLabel: {
            type: String,
            default: ''
        },
        maxVisiblePages: {
            type: Number,
            default: 5
        }
    },
    computed: {
        normalizedTotalItems() {
            return normalizePaginationTotal(this.totalItems)
        },
        normalizedPageNumber() {
            return Math.min(normalizePaginationPage(this.pageNumber), this.totalPages)
        },
        normalizedItemsPerPage() {
            return normalizeItemsPerPage(this.itemsPerPage)
        },
        totalPages() {
            return getPaginationTotalPages(this.normalizedTotalItems, this.normalizedItemsPerPage)
        },
        hasPagination() {
            return this.normalizedTotalItems > this.normalizedItemsPerPage
        },
        canGoPrevious() {
            return this.normalizedPageNumber > 1
        },
        canGoNext() {
            return this.normalizedPageNumber < this.totalPages
        },
        visiblePageNumbers() {
            const pages = getVisiblePageNumbers(this.normalizedPageNumber, this.totalPages, this.maxVisiblePages)
            if (this.compact && pages[pages.length - 1] < this.totalPages) {
                if (pages[pages.length - 1] < this.totalPages - 1) pages.push('ellipsis')
                pages.push(this.totalPages)
            }
            return pages
        }
    },
    methods: {
        goToPage(page) {
            if (page !== this.normalizedPageNumber && page >= 1 && page <= this.totalPages) {
                this.$emit('page-change', page)
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.pagination-container {
    padding-top: 8px;
    height: 53px;
    flex-shrink: 0;
    display: flex;
    background-color: #f4f2ed;

    .pagination-controls {
        width: 100%;
        display: grid;
        grid-template-columns: 1fr 1fr;
        place-items: center;
        gap: 8px;
    }

    button {
        display: grid;
        place-items: center;
        border: 0;
        border-radius: 4px;
        padding: 8px;
        cursor: pointer;
        width: 100%;
        height: 100%;
        background-color: #ede7db;
        color: $neutro-s80;
        font-family: $text-sbold;
    }

    .back.button {
        grid-column: 1;
    }

    .forward.button {
        grid-column: 2;
    }

    &.detailed {
        box-sizing: border-box;
        display: flex;
        align-items: center;
        width: 100%;
        min-height: 48px;
        height: 48px;
        padding: 8px;
        gap: 0;
        background-color: #edeae3;
        border-top: 1px solid #b8b8b8;

        .pagination-total {
            flex: 0 0 auto;
            color: #727477;
            font-family: $text-bold;
            font-size: 16px;
            line-height: 19px;
            white-space: nowrap;
        }

        .pagination-controls {
            flex: 1 1 auto;
            display: flex;
            justify-content: center;
            gap: 8px;
        }

        button {
            box-sizing: border-box;
            width: 32px;
            min-width: 32px;
            height: 32px;
            padding: 4px;
            background-color: transparent;
            color: #727477;
            font-family: $text;
            font-size: 16px;
            font-weight: 400;
            line-height: 18px;

            &.active {
                background-color: #fff;
                border: 1px solid #b8b8b8;
                color: #171718;
            }
        }

        .back.button,
        .forward.button {
            grid-column: auto;

            i {
                width: 16px;
                height: 16px;
                font-size: 16px;
                line-height: 16px;
            }
        }
    }
}

@media (max-width: 767px) {
    .pagination-container.detailed {
        display: grid;
        grid-template-columns: 1fr;
        gap: 6px;
        height: auto;

        .pagination-total,
        .pagination-controls {
            grid-column: 1;
        }

        .pagination-total {
            justify-self: center;
        }

        button {
            width: 32px;
            min-width: 32px;
            height: 32px;
        }
    }
}
</style>

<style lang="scss" scoped>
.pagination-container.detailed.compact {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);

    .pagination-total {
        grid-column: 1;
        justify-self: start;
    }
    .pagination-controls {
        grid-column: 2;
        width: auto;
        justify-content: center;
    }
    .pagination-ellipsis {
        color: #727477;
    }
}

@media (max-width: 900px) {
    .pagination-container.detailed.compact {
        display: flex;
        height: 48px;
        min-height: 48px;
        gap: 8px;

        .pagination-total {
            font-size: 12px;
        }
        .pagination-controls {
            gap: 2px;
            min-width: 0;
        }
        button {
            width: 24px;
            min-width: 24px;
            height: 32px;
            padding: 2px;
            font-size: 12px;
        }
    }
}

@media (max-width: 380px) {
    .pagination-container.detailed.compact {
        .pagination-total { font-size: 11px; }
        button { width: 18px; min-width: 18px; padding: 0; }
    }
}
</style>
